import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const SIZE_CLASSES = {
  xs: 'h-6 w-6 text-[9px]',
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-11 w-11 text-base',
} as const;

export type UserAvatarSize = keyof typeof SIZE_CLASSES;

function getInitials(name: string): string {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

interface UserAvatarProps {
  name: string;
  picture?: string | null;
  size?: UserAvatarSize;
  isEntrepreneur?: boolean;
  className?: string;
  tooltip?: React.ReactNode;
}

export function UserAvatar({
  name,
  picture,
  size = 'sm',
  isEntrepreneur,
  className,
  tooltip,
}: UserAvatarProps) {
  const avatar = (
    <Avatar className={cn(SIZE_CLASSES[size], 'cursor-default flex-shrink-0', className)}>
      {picture && <AvatarImage src={picture} alt={name} />}
      <AvatarFallback
        className={
          isEntrepreneur
            ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
            : 'bg-foreground/10 text-foreground'
        }
      >
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );

  if (!tooltip) return avatar;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
